/**
 * The one place the customer's car becomes order data (§5.3.6).
 *
 * Three paths add to cart — the fitment widget, the product form, and the cart
 * cross-sell — and each used to build its own line-item properties. They
 * disagreed: `Generation` meant "what this part covers" on one and "what the
 * customer told us" on another, `Year` was fabricated from the start of a
 * generation range on one and omitted on the others, and the cart attribute
 * carried make+model only. An order that says two different things about the
 * same car is worse than one that says less.
 *
 * ── What the properties are FOR ──────────────────────────────────────────────
 *
 * The client picks from the product TITLE against their own catalogue. These
 * properties are the customer's side of the transaction: the car they told us
 * they drive, so the pick can be sanity-checked and a support question can be
 * answered without guessing. That makes two rules absolute:
 *
 *   1. 🔴 Never attach a car this product does not fit. A mismatch is not extra
 *      context, it is a false statement on the packing slip — and a customer
 *      buying a second car's part from a page they reached by search is a
 *      perfectly ordinary thing to do.
 *   2. 🔴 Never invent a value. The manual cascade yields a GENERATION, not a
 *      year, so `Years` carries the range that was actually selected and `Year`
 *      appears only when a plate lookup supplied a real one.
 *
 * No build step; loaded from the layout head so every path has it.
 */
(() => {
  'use strict';
  if (window.AirfluxOrder) return;

  const CHANNEL_LABEL = { 'in-channel': 'In-Channel', 'out-channel': 'Out-Channel' };

  /** Drops blank/nullish values so a property is absent rather than empty. */
  const compact = (obj) =>
    Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== ''));

  /**
   * Line-item properties for one add-to-cart.
   *
   * @param {object|null} car   the stored car (airflux:car)
   * @param {object} [product]  facts about the item being added:
   *                            { channel, pack } — both read off the product,
   *                            because the packing slip must say which kit.
   * @returns {object} properties, or {} when there is no car to attach
   */
  function carProperties(car, product = {}) {
    if (!car?.handle) return {};
    return compact({
      Make: car.make,
      Model: car.model,
      // Only ever a year somebody actually stated. The plate lookup gives one;
      // the manual cascade does not, and the start of a range is not a fact
      // about the customer's car.
      Year: car.year ? String(car.year) : null,
      // What the customer DID select on the manual path — the generation's
      // stated range, verbatim from the dropdown they used.
      Years: car.years || null,
      // The version they told us, never the part's own coverage. The part's
      // coverage is in the title, which is what the picker reads anyway.
      Generation: car.versionCode || null,
      Body: car.versionBody || car.body || null,
      Channel: CHANNEL_LABEL[product.channel] || null,
      Pack: product.pack ? `${product.pack}-piece set` : null,
      _fitment_key: car.handle,
    });
  }

  /**
   * Order-level attributes. Mirrors the car onto the cart so it survives to the
   * order even when a line item was added by a path we don't control.
   *
   * The label carries the years and the version too: "BMW 3 Series" alone spans
   * three generations that take different parts, which is not an answer.
   */
  function cartAttributes(car) {
    if (!car?.handle) return {};
    return {
      Vehicle: [car.label, car.years, car.version].filter(Boolean).join(' · '),
      _fitment_key: car.handle,
    };
  }

  window.AirfluxOrder = { carProperties, cartAttributes, CHANNEL_LABEL };
})();
